/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as eqpApi from '../../lib/api/eqp';
import * as eqpAgentApi from '../../lib/api/eqpAgent';
import * as codeApi from '../../lib/api/commonCode';
import EqpListPanel from '../../components/eqpmng/EqpListPanel';
import EqpDetailModal from '../../components/eqpmng/EqpDetailModal';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { useMenuAccess } from '../../hooks/useMenuAccess';

// 하드코딩 기본값 (공통코드 로드 전 fallback)
export const EQP_TYPE_LABELS = {
  PRESS:  '프레스',
  CNC:    'CNC',
  WELD:   '용접',
  INSP:   '검사장비',
  CONV:   '컨베이어',
  ROBOT:  '로봇',
  ETC:    '기타',
};

export const LOG_TYPE_LABELS = {
  OPER:     '가동',
  STOP:     '정지',
  FAILURE:  '고장',
  PM:       '예방점검',
  INSPECT:  '정기검사',
  REPAIR:   '수리',
  TOOL_CHG: '공구교체',
  SETUP:    '설비조정',
  CLEANING: '청소',
  ETC:      '기타',
};

export const LOG_TYPE_COLORS = {
  OPER:     'success',
  STOP:     'default',
  FAILURE:  'error',
  PM:       'info',
  INSPECT:  'info',
  REPAIR:   'warning',
  TOOL_CHG: 'warning',
  SETUP:    'secondary',
  CLEANING: 'secondary',
  ETC:      'default',
};

export const DOWNTIME_TYPES = new Set(['FAILURE', 'REPAIR']);

// 공통코드 → label/color 맵 변환 헬퍼
const buildLabelMap = (list) =>
  Object.fromEntries(list.map((r) => [r.slcCd, r.itemNm]));
const buildColorMap = (list) =>
  Object.fromEntries(list.map((r) => [r.slcCd, r.revVal1 || 'default']));

const EqpMngContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  // 장비관리 메뉴 쓰기 권한 - 메뉴에서는 빼놓고 주소만 직접 들어온 경우에 대한 2차 방어선이다.
  // 자세한 한계는 useMenuAccess.js 참고(이 프로젝트는 메뉴별 read/write 구분이 없다).
  const canManage = useMenuAccess('eqpmng');

  const [eqpList, setEqpList]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchEqpTyp, setSearchEqpTyp]   = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEqp, setSelectedEqp]         = useState(null);
  const [tick, setTick] = useState(0);

  // 공통코드 동적 로드 (EQP_TYP / EQP_LOG_TYP / EQP_STATUS)
  const [eqpTypeOptions, setEqpTypeOptions]   = useState(
    Object.entries(EQP_TYPE_LABELS).map(([v, l]) => ({ slcCd: v, itemNm: l }))
  );
  const [logTypeLabels, setLogTypeLabels]     = useState(LOG_TYPE_LABELS);
  const [logTypeColors, setLogTypeColors]     = useState(LOG_TYPE_COLORS);
  const [eqpStatusOptions, setEqpStatusOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);

  // 기본정보 탭의 "담당 에이전트" 선택지 - 목록 자체는 조회만 하면 되므로 조용히 실패해도 넘어간다
  useEffect(() => {
    eqpAgentApi.getAgentList()
      .then((res) => setAgentOptions(eqpAgentApi.extractAgentList(res)))
      .catch(() => {});
  }, [tick]);

  useEffect(() => {
    const loadCodes = async () => {
      try {
        const [typ, log, status] = await Promise.allSettled([
          codeApi.getComCodes({ blcCd: 'EQP_TYP' }),
          codeApi.getComCodes({ blcCd: 'EQP_LOG_TYP' }),
          codeApi.getComCodes({ blcCd: 'EQP_STATUS' }),
        ]);
        if (typ.value?.data) {
          const list = typ.value.data.list || typ.value.data || [];
          if (list.length > 0) setEqpTypeOptions(list);
        }
        if (log.value?.data) {
          const list = log.value.data.list || log.value.data || [];
          if (list.length > 0) {
            setLogTypeLabels(buildLabelMap(list));
            setLogTypeColors(buildColorMap(list));
          }
        }
        if (status.value?.data) {
          const list = status.value.data.list || status.value.data || [];
          if (list.length > 0) setEqpStatusOptions(list);
        }
      } catch { /* fallback to hardcoded */ }
    };
    loadCodes();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await eqpApi.getEqpList({
          searchKeyword: searchKeyword || undefined,
          searchEqpTyp:  searchEqpTyp  || undefined,
        });
        setEqpList(res.data.list || []);
      } catch (e) {
        enqueueSnackbar('장비 목록 조회 실패', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [searchKeyword, searchEqpTyp, tick]);

  const handleEqpSave = async (values) => {
    if (!canManage) {
      enqueueSnackbar('장비관리 쓰기 권한이 없습니다.', { variant: 'error' });
      return;
    }
    try {
      if (selectedEqp) {
        await eqpApi.editEqp(selectedEqp.eqpUuid, values);
        enqueueSnackbar('장비 정보를 수정했습니다.', { variant: 'success' });
      } else {
        await eqpApi.addEqp(values);
        enqueueSnackbar('장비를 등록했습니다.', { variant: 'success' });
      }
      setDetailModalOpen(false);
      setSelectedEqp(null);
      setTick(t => t + 1);
    } catch (e) {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    }
  };

  // 목록 휴지통 아이콘(확인창 거친 뒤 호출됨)과 상세창 [삭제] 버튼이 함께 쓴다 - 상세창에서
  // 삭제했을 때는 그 창도 닫아야 한다(목록에서 지웠을 때는 모달이 안 열려 있어 아무 effect 없음).
  const handleEqpDelete = async (eqpUuid) => {
    if (!canManage) {
      enqueueSnackbar('장비관리 쓰기 권한이 없습니다.', { variant: 'error' });
      return;
    }
    try {
      await eqpApi.removeEqp(eqpUuid);
      enqueueSnackbar('장비를 삭제했습니다.', { variant: 'success' });
      setDetailModalOpen(false);
      setSelectedEqp(null);
      setTick(t => t + 1);
    } catch (e) {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  // 목록 행에는 통신 채널(connList)이 없다. 행을 그대로 넘기면 저장한 채널이 비어 보이고,
  // 그 상태로 다시 저장하면 빈 목록이 가서 서버가 채널을 지운다(2026-10-11 현장 확인).
  // 그래서 상세(채널 포함)를 받아서 연다.
  const handleRowClick = async (row) => {
    try {
      const res = await eqpApi.getEqp(row.eqpUuid);
      const info = res?.data?.info;
      if (!info) throw new Error('empty');
      setSelectedEqp(info);
      setDetailModalOpen(true);
    } catch (e) {
      enqueueSnackbar('장비 상세를 불러오지 못했습니다. 잠시 후 다시 시도하세요.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <BreadcrumbsContainer title="기준정보/장비관리" />
      <Typography variant="h5" fontWeight={700} mb={2}>장비 관리</Typography>

      <EqpListPanel
        list={eqpList}
        loading={loading}
        searchKeyword={searchKeyword}
        searchEqpTyp={searchEqpTyp}
        onSearchKeywordChange={setSearchKeyword}
        onSearchEqpTypChange={setSearchEqpTyp}
        onAdd={() => { setSelectedEqp(null); setDetailModalOpen(true); }}
        onRowClick={handleRowClick}
        onDelete={handleEqpDelete}
        eqpTypeOptions={eqpTypeOptions}
        canManage={canManage}
      />

      <EqpDetailModal
        open={detailModalOpen}
        eqpData={selectedEqp}
        onClose={() => { setDetailModalOpen(false); setSelectedEqp(null); }}
        onSave={handleEqpSave}
        onDelete={handleEqpDelete}
        eqpTypeOptions={eqpTypeOptions}
        eqpStatusOptions={eqpStatusOptions}
        agentOptions={agentOptions}
        logTypeLabels={logTypeLabels}
        logTypeColors={logTypeColors}
        mode="manage"
        canManage={canManage}
      />
    </Box>
  );
};

export default EqpMngContainer;
